/**
 * Normalizes canonical Contact Journey Events into the Jornada 360 UI model.
 * API categories are multi-source; UI keeps broad tabs: acquisition / automation /
 * commercial / support. Source/category metadata is retained for display.
 */

export const JORNADA_CATEGORIES = [
  'all',
  'acquisition',
  'automation',
  'commercial',
  'support',
];

const ACQUISITION_HINTS =
  /lead|captura|capture|opt[_\s-]?in|form|landing|webinar|ads?|meta|facebook|instagram|utm|traffic|aquisic|acquisition|cadastro|signup|register|score|group_joined|group_left|group_engaged|group_highly_engaged|whatsapp_groups|wa-group/i;
const COMMERCIAL_HINTS =
  /purchase|order|payment|paid|sale|checkout|invoice|offer|product|compra|pedido|pagamento|venda|comercial|upsell|downsell|cart|carrinho|amount|billing|commerce|appointment/i;
const VALUE_SUCCESS_STATUSES = new Set(['completed', 'success']);
const VALUE_EXCLUDED_HINTS =
  /refund|chargeback|cancel|cancelled|canceled|reversal|estorno|reembolso|charge[_-]?back/i;

const SUPPORT_API_CATEGORIES = new Set([
  'conversation',
  'voip',
  'task',
  'note',
  'kanban',
]);

export const dayKeyFromDate = value => {
  if (!value) return 'unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return date.toISOString().slice(0, 10);
};

const payloadOf = event => event?.payload || {};

const linksOf = event => event?.links || {};

const technicalOf = event => event?.technical || null;

/**
 * Map API category/type/payload into existing broad UI categories.
 * - commerce -> commercial
 * - automation may stay acquisition when type/payload clearly matches
 * - conversation, voip, task, note -> support
 * - score -> acquisition; appointment -> commercial
 */
export const mapJourneyCategoryToUi = event => {
  const apiCategory = String(event?.category || '').toLowerCase();
  const payload = payloadOf(event);
  const type = event?.type || payload.event_type || '';
  const haystack = [
    apiCategory,
    type,
    event?.summary,
    payload.source,
    payload.event_type,
    payload.product_name,
    payload.product_code,
    payload.offer_code,
  ]
    .filter(Boolean)
    .join(' ');

  if (apiCategory === 'commerce') return 'commercial';
  if (apiCategory === 'appointment') return 'commercial';
  if (apiCategory === 'score') return 'acquisition';
  if (apiCategory === 'kanban') return 'support';
  if (SUPPORT_API_CATEGORIES.has(apiCategory)) return 'support';

  if (apiCategory === 'automation') {
    if (ACQUISITION_HINTS.test(haystack) && !COMMERCIAL_HINTS.test(haystack)) {
      return 'acquisition';
    }
    return 'automation';
  }

  // Fallback for unknown / legacy shapes
  if (
    COMMERCIAL_HINTS.test(haystack) ||
    payload.amount_cents != null ||
    event?.amount_cents != null
  ) {
    return 'commercial';
  }
  if (ACQUISITION_HINTS.test(haystack)) return 'acquisition';
  if (event?.conversation_id || linksOf(event).conversation_id) {
    return 'support';
  }
  return 'automation';
};

/** @deprecated Prefer mapJourneyCategoryToUi for Journey events */
export const categorizeEvent = event => {
  // Legacy AutomationEvent-shaped objects still used in older fixtures.
  if (event?.category && !event?.source && event?.occurred_at) {
    return mapJourneyCategoryToUi(event);
  }
  if (event?.category && event?.type && event?.occurred_at != null) {
    return mapJourneyCategoryToUi(event);
  }

  const haystack = [
    event?.source,
    event?.event_type,
    event?.product_name,
    event?.product_code,
    event?.offer_code,
  ]
    .filter(Boolean)
    .join(' ');

  if (COMMERCIAL_HINTS.test(haystack) || event?.amount_cents != null) {
    return 'commercial';
  }
  if (ACQUISITION_HINTS.test(haystack)) return 'acquisition';
  if (
    /conversation|message|ticket|support|atendimento|chat|whatsapp|email|reply|agent|inbox|humano|handoff|transfer/i.test(
      haystack
    ) ||
    event?.conversation_id
  ) {
    return 'support';
  }
  return 'automation';
};

const numericOrNull = value => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const isAutomationResource = links =>
  String(links?.resource_type || '') === 'automation_event' &&
  numericOrNull(links?.resource_id) != null;

export const normalizeJornadaEvent = event => {
  if (!event?.id) return null;

  // Journey API contract
  const isJourneyShape =
    Object.prototype.hasOwnProperty.call(event, 'occurred_at') ||
    Object.prototype.hasOwnProperty.call(event, 'category') ||
    Object.prototype.hasOwnProperty.call(event, 'links');

  if (isJourneyShape && (event.occurred_at != null || event.category)) {
    const payload = payloadOf(event);
    const links = linksOf(event);
    const technical = technicalOf(event);
    const occurredAt = event.occurred_at;
    const category = mapJourneyCategoryToUi(event);
    const amountCents = numericOrNull(payload.amount_cents);
    const rawProduct =
      links.resource_type === 'automation_event'
        ? payload.product_name || payload.product_code || null
        : payload.product_name || payload.product_code || payload.title || null;
    const isKanbanOfferFallback =
      links.resource_type === 'kanban_item' &&
      String(event.type || '').startsWith('kanban.offer.') &&
      rawProduct === 'Oferta';
    const product = isKanbanOfferFallback ? null : rawProduct;
    const resourceId = numericOrNull(links.resource_id);
    const conversationDisplayId =
      links.conversation_display_id == null ||
      links.conversation_display_id === ''
        ? null
        : links.conversation_display_id;

    return {
      id: event.id,
      category,
      apiCategory: event.category || null,
      title: event.summary || event.type || String(event.id),
      product,
      status: event.status || null,
      occurredAt,
      dayKey: dayKeyFromDate(occurredAt),
      amountCents,
      currency: payload.currency || null,
      conversationId: links.conversation_id || null,
      conversationDisplayId,
      contactId: links.contact_id || null,
      launchId: payload.launch_id || null,
      transactionId: payload.transaction_id || null,
      source: payload.source || event.source || event.category || null,
      eventType: event.type || payload.event_type || null,
      stepsSummary: payload.steps_summary || null,
      currentError: payload.current_error || null,
      actor: event.actor || null,
      links,
      resourceType: links.resource_type || null,
      resourceId,
      canViewExecution: isAutomationResource(links),
      customerName: payload.customer_name || null,
      customerEmail: payload.customer_email || null,
      customerPhone: payload.customer_phone || null,
      externalEventId:
        technical?.external_event_id || payload.external_event_id || null,
      idempotencyKey:
        technical?.idempotency_key || payload.idempotency_key || null,
      processedAt: payload.processed_at || technical?.processed_at || null,
      payload,
      technical,
      raw: event,
    };
  }

  // Legacy AutomationEvent shape (kept for transitional fixtures / specs).
  const occurredAt =
    event.received_at || event.processed_at || event.updated_at;
  const category = categorizeEvent(event);
  const title = [event.source, event.event_type].filter(Boolean).join(' · ');
  const product = event.product_name || event.product_code || null;
  const legacyResourceId = numericOrNull(event.id);

  return {
    id: event.id,
    category,
    apiCategory: null,
    title: title || String(event.id),
    product,
    status: event.status || null,
    occurredAt,
    dayKey: dayKeyFromDate(occurredAt),
    amountCents:
      event.amount_cents == null || event.amount_cents === ''
        ? null
        : Number(event.amount_cents),
    currency: event.currency || null,
    conversationId: event.conversation_id || null,
    conversationDisplayId:
      event.conversation_display_id == null ||
      event.conversation_display_id === ''
        ? null
        : event.conversation_display_id,
    contactId: event.contact_id || null,
    launchId: event.launch_id || null,
    transactionId: event.transaction_id || null,
    source: event.source || null,
    eventType: event.event_type || null,
    stepsSummary: event.steps_summary || null,
    currentError: event.current_error || null,
    actor: null,
    links: null,
    resourceType: 'automation_event',
    resourceId: legacyResourceId,
    canViewExecution: legacyResourceId != null,
    customerName: event.customer_name || null,
    customerEmail: event.customer_email || null,
    customerPhone: event.customer_phone || null,
    externalEventId: event.external_event_id || null,
    idempotencyKey: event.idempotency_key || null,
    processedAt: event.processed_at || null,
    payload: null,
    technical: null,
    raw: event,
  };
};

export const normalizeJornadaEvents = events =>
  (Array.isArray(events) ? events : [])
    .map(normalizeJornadaEvent)
    .filter(Boolean)
    .sort(
      (a, b) =>
        new Date(b.occurredAt || 0).getTime() -
        new Date(a.occurredAt || 0).getTime()
    );

/** Stable append + dedupe by event id (keeps first occurrence). */
export const mergeJornadaEvents = (existing, incoming) => {
  const seen = new Set();
  const merged = [];
  [...(existing || []), ...(incoming || [])].forEach(item => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    merged.push(item);
  });
  return merged.sort(
    (a, b) =>
      new Date(b.occurredAt || 0).getTime() -
      new Date(a.occurredAt || 0).getTime()
  );
};

export const filterJornadaByCategory = (items, category = 'all') => {
  if (!category || category === 'all') return items;
  return items.filter(item => item.category === category);
};

export const groupJornadaByDay = items => {
  const groups = new Map();

  items.forEach(item => {
    const key = item.dayKey || 'unknown';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });

  return Array.from(groups.entries())
    .map(([dayKey, dayItems]) => ({
      dayKey,
      items: dayItems,
      count: dayItems.length,
    }))
    .sort((a, b) => {
      if (a.dayKey === 'unknown') return 1;
      if (b.dayKey === 'unknown') return -1;
      return b.dayKey.localeCompare(a.dayKey);
    });
};

const isExcludedCommercialValueEvent = item => {
  const haystack = [
    item?.source,
    item?.eventType,
    item?.product,
    item?.title,
    item?.apiCategory,
  ]
    .filter(Boolean)
    .join(' ');
  return VALUE_EXCLUDED_HINTS.test(haystack);
};

/**
 * Sum commercial value only from successful/completed events with positive
 * amount. Excludes refund/chargeback/cancel/reversal. Dedupes by
 * transaction_id (preferred) or event id. Omits the chip when currencies mix.
 * Adapted for Journey payload/status (status already normalized by API).
 */
export const buildJornadaValueSummary = items => {
  const candidates = (Array.isArray(items) ? items : []).filter(item => {
    if (item?.category !== 'commercial') return false;
    if (!VALUE_SUCCESS_STATUSES.has(item?.status)) return false;
    if (item.amountCents == null || Number.isNaN(item.amountCents))
      return false;
    if (Number(item.amountCents) <= 0) return false;
    if (isExcludedCommercialValueEvent(item)) return false;
    return true;
  });

  if (!candidates.length) return null;

  const seen = new Set();
  const unique = [];
  candidates.forEach(item => {
    const key =
      item.transactionId != null && item.transactionId !== ''
        ? `tx:${item.transactionId}`
        : `id:${item.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(item);
  });

  if (!unique.length) return null;

  // Missing currency is treated as BRL before mixed-currency detection so
  // USD+nil omits the chip while all-nil aggregates as BRL.
  const currencies = new Set(unique.map(item => item.currency || 'BRL'));
  if (currencies.size > 1) return null;

  const currency = currencies.values().next().value || 'BRL';
  const totalCents = unique.reduce(
    (sum, item) => sum + Number(item.amountCents || 0),
    0
  );

  return { totalCents, currency, count: unique.length };
};

/**
 * Optional summary chips — only include fields that callers already have.
 * score/stage come from existing props; value derives from loaded events.
 */
export const buildJornadaSummary = (
  items,
  { score = null, stage = null } = {}
) => {
  const value = buildJornadaValueSummary(items);
  const latest = items[0] || null;

  return {
    totalEvents: items.length,
    score: score == null || score === '' ? null : score,
    stage: stage == null || stage === '' ? null : stage,
    value,
    latest,
    categories: JORNADA_CATEGORIES.filter(key => key !== 'all').map(key => ({
      key,
      count: items.filter(item => item.category === key).length,
    })),
  };
};

export const expandJornadaItems = (items, visibleCount) => {
  const limit = Math.max(0, Number(visibleCount) || 0);
  return {
    visible: items.slice(0, limit),
    hiddenCount: Math.max(0, items.length - limit),
    hasMore: items.length > limit,
  };
};

const TITLE_KEYS = Object.freeze({
  generic: 'AUTOMATION_EVENTS.EVENT_TITLES.GENERIC_EVENT',
  automation: 'AUTOMATION_EVENTS.EVENT_TITLES.AUTOMATION_EVENT',
  automationAmount:
    'AUTOMATION_EVENTS.EVENT_TITLES.AUTOMATION_EVENT_WITH_AMOUNT',
  automationAmountOnly:
    'AUTOMATION_EVENTS.EVENT_TITLES.AUTOMATION_EVENT_AMOUNT_ONLY',
  automationPlain: 'AUTOMATION_EVENTS.EVENT_TITLES.AUTOMATION_EVENT_PLAIN',
  conversationOpened: 'AUTOMATION_EVENTS.EVENT_TITLES.CONVERSATION_OPENED',
  conversationFirstReply:
    'AUTOMATION_EVENTS.EVENT_TITLES.CONVERSATION_FIRST_REPLY',
  note: 'AUTOMATION_EVENTS.EVENT_TITLES.NOTE_CREATED',
  noteGeneric: 'AUTOMATION_EVENTS.EVENT_TITLES.NOTE_CREATED_GENERIC',
  taskCreated: 'AUTOMATION_EVENTS.EVENT_TITLES.TASK_CREATED',
  taskActivity: 'AUTOMATION_EVENTS.EVENT_TITLES.TASK_ACTIVITY',
  taskActivityGeneric: 'AUTOMATION_EVENTS.EVENT_TITLES.TASK_ACTIVITY_GENERIC',
  leadScore: 'AUTOMATION_EVENTS.EVENT_TITLES.LEAD_SCORE',
  leadScoreProfile: 'AUTOMATION_EVENTS.EVENT_TITLES.LEAD_SCORE_WITH_PROFILE',
  leadScorePending: 'AUTOMATION_EVENTS.EVENT_TITLES.LEAD_SCORE_PENDING',
  leadScorePendingProfile:
    'AUTOMATION_EVENTS.EVENT_TITLES.LEAD_SCORE_PENDING_WITH_PROFILE',
  appointment: 'AUTOMATION_EVENTS.EVENT_TITLES.APPOINTMENT',
  appointmentDate: 'AUTOMATION_EVENTS.EVENT_TITLES.APPOINTMENT_WITH_DATE',
  call: 'AUTOMATION_EVENTS.EVENT_TITLES.CALL',
  callDuration: 'AUTOMATION_EVENTS.EVENT_TITLES.CALL_WITH_DURATION',
  callDisposition: 'AUTOMATION_EVENTS.EVENT_TITLES.CALL_WITH_DISPOSITION',
  callDurationDisposition:
    'AUTOMATION_EVENTS.EVENT_TITLES.CALL_WITH_DURATION_AND_DISPOSITION',
  offer: 'AUTOMATION_EVENTS.EVENT_TITLES.KANBAN_OFFER',
  offerAmount: 'AUTOMATION_EVENTS.EVENT_TITLES.KANBAN_OFFER_WITH_AMOUNT',
  offerClosed: 'AUTOMATION_EVENTS.EVENT_TITLES.KANBAN_OFFER_CLOSED',
  offerClosedAmount:
    'AUTOMATION_EVENTS.EVENT_TITLES.KANBAN_OFFER_CLOSED_WITH_AMOUNT',
  stageChanged: 'AUTOMATION_EVENTS.EVENT_TITLES.KANBAN_STAGE_CHANGED',
  itemCreated: 'AUTOMATION_EVENTS.EVENT_TITLES.KANBAN_ITEM_CREATED',
});

const LABEL_KEYS = Object.freeze({
  systemActor: 'JORNADA_360.ACTORS.SYSTEM',
  taskUntitled: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_UNTITLED',
  offerFallback: 'AUTOMATION_EVENTS.EVENT_LABELS.KANBAN_OFFER_FALLBACK',
  kanbanCard: 'AUTOMATION_EVENTS.EVENT_LABELS.KANBAN_CARD',
  kanbanCardUnknown: 'AUTOMATION_EVENTS.EVENT_LABELS.KANBAN_CARD_UNKNOWN',
  kanbanStageUnknown: 'AUTOMATION_EVENTS.EVENT_LABELS.KANBAN_STAGE_UNKNOWN',
});

const TASK_ACTION_KEYS = Object.freeze({
  created: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.CREATED',
  updated: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.UPDATED',
  completed: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.COMPLETED',
  cancelled: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.CANCELLED',
  deleted: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.DELETED',
  assigned: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.ASSIGNED',
  unassigned: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.UNASSIGNED',
  reopened: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.REOPENED',
  commented: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.COMMENTED',
  status_changed: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.STATUS_CHANGED',
  due_changed: 'AUTOMATION_EVENTS.EVENT_LABELS.TASK_ACTIONS.DUE_CHANGED',
});

const LEAD_PROFILE_KEYS = Object.freeze({
  hot: 'AUTOMATION_EVENTS.EVENT_LABELS.LEAD_PROFILES.HOT',
  warm: 'AUTOMATION_EVENTS.EVENT_LABELS.LEAD_PROFILES.WARM',
  cold: 'AUTOMATION_EVENTS.EVENT_LABELS.LEAD_PROFILES.COLD',
  disqualified: 'AUTOMATION_EVENTS.EVENT_LABELS.LEAD_PROFILES.DISQUALIFIED',
});

const LEAD_PROFILE_ALIASES = Object.freeze({
  hot: 'hot',
  quente: 'hot',
  warm: 'warm',
  morno: 'warm',
  cold: 'cold',
  frio: 'cold',
  disqualified: 'disqualified',
  desqualificado: 'disqualified',
});

const APPOINTMENT_STATUS_KEYS = Object.freeze({
  scheduled: 'AUTOMATION_EVENTS.EVENT_LABELS.APPOINTMENT_STATUSES.SCHEDULED',
  attended: 'AUTOMATION_EVENTS.EVENT_LABELS.APPOINTMENT_STATUSES.ATTENDED',
  no_show: 'AUTOMATION_EVENTS.EVENT_LABELS.APPOINTMENT_STATUSES.NO_SHOW',
  cancelled: 'AUTOMATION_EVENTS.EVENT_LABELS.APPOINTMENT_STATUSES.CANCELLED',
});

const CALL_DIRECTION_KEYS = Object.freeze({
  inbound: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_DIRECTIONS.INBOUND',
  outbound: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_DIRECTIONS.OUTBOUND',
});

const CALL_STATUS_KEYS = Object.freeze({
  in_progress: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.IN_PROGRESS',
  ended: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.ENDED',
  missed: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.MISSED',
  answered: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.ENDED',
  rejected: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.REJECTED',
  failed: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.FAILED',
  incoming: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.INCOMING',
  ringing: 'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.RINGING',
});

export const JOURNEY_STATUS_I18N_KEYS = Object.freeze({
  received: 'AUTOMATION_EVENTS.STATUSES.RECEIVED',
  processing: 'AUTOMATION_EVENTS.STATUSES.PROCESSING',
  completed: 'AUTOMATION_EVENTS.STATUSES.COMPLETED',
  failed: 'AUTOMATION_EVENTS.STATUSES.FAILED',
  manual_action: 'AUTOMATION_EVENTS.STATUSES.MANUAL_ACTION',
  ignored: 'AUTOMATION_EVENTS.STATUSES.IGNORED',
  pending: 'AUTOMATION_EVENTS.STATUSES.PENDING',
  running: 'AUTOMATION_EVENTS.STATUSES.RUNNING',
  success: 'AUTOMATION_EVENTS.STATUSES.SUCCESS',
  skipped: 'AUTOMATION_EVENTS.STATUSES.SKIPPED',
  active: 'AUTOMATION_EVENTS.STATUSES.ACTIVE',
  cancelled: 'AUTOMATION_EVENTS.STATUSES.CANCELLED',
  unknown: 'AUTOMATION_EVENTS.STATUSES.UNKNOWN',
});

export const JOURNEY_CATEGORY_I18N_KEYS = Object.freeze({
  all: 'JORNADA_360.CATEGORIES.ALL',
  acquisition: 'JORNADA_360.CATEGORIES.ACQUISITION',
  automation: 'JORNADA_360.CATEGORIES.AUTOMATION',
  commercial: 'JORNADA_360.CATEGORIES.COMMERCIAL',
  support: 'JORNADA_360.CATEGORIES.SUPPORT',
});

const STATIC_I18N_KEY_GROUPS = [
  TITLE_KEYS,
  LABEL_KEYS,
  TASK_ACTION_KEYS,
  LEAD_PROFILE_KEYS,
  APPOINTMENT_STATUS_KEYS,
  CALL_DIRECTION_KEYS,
  CALL_STATUS_KEYS,
  JOURNEY_STATUS_I18N_KEYS,
  JOURNEY_CATEGORY_I18N_KEYS,
];

export const JORNADA_CONSUMED_I18N_KEYS = Object.freeze([
  ...new Set(STATIC_I18N_KEY_GROUPS.flatMap(group => Object.values(group))),
  'AUTOMATION_EVENTS.EVENT_LABELS.APPOINTMENT_STATUSES.UNKNOWN',
  'AUTOMATION_EVENTS.EVENT_LABELS.CALL_DIRECTIONS.UNKNOWN',
  'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.UNKNOWN',
  'AUTOMATION_EVENTS.EMPTY_VALUE',
]);

const INTL_LOCALES = Object.freeze({
  en: 'en',
  es: 'es',
  pt: 'pt-BR',
  pt_BR: 'pt-BR',
  'pt-BR': 'pt-BR',
});

export const normalizeJourneyLocale = locale =>
  INTL_LOCALES[String(locale || 'en')] || 'en';

export const formatJourneyMoney = (cents, currency = 'BRL', locale = 'en') => {
  if (cents == null || cents === '') return null;
  const value = Number(cents) / 100;
  if (!Number.isFinite(value)) return null;

  const intlLocale = normalizeJourneyLocale(locale);
  const currencyCode = currency || 'BRL';
  try {
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: currencyCode,
    }).format(value);
  } catch {
    const amount = new Intl.NumberFormat(intlLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `${currencyCode} ${amount}`;
  }
};

const JOURNEY_DATE_TIME_OPTIONS = Object.freeze({
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const formatJourneyDateTime = (
  value,
  locale = 'en',
  options = JOURNEY_DATE_TIME_OPTIONS
) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(normalizeJourneyLocale(locale), options);
};

const translate = (t, key, params) => {
  // Keys are finite and verified against every catalog by parity specs.
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(key, params);
};

export const localizedJourneyStatus = (status, t) => {
  if (!status) return translate(t, 'AUTOMATION_EVENTS.EMPTY_VALUE');
  const key = JOURNEY_STATUS_I18N_KEYS[String(status).toLowerCase()];
  return translate(t, key || JOURNEY_STATUS_I18N_KEYS.unknown);
};

export const localizedJourneyCategory = (category, t) => {
  const key = JOURNEY_CATEGORY_I18N_KEYS[String(category || '').toLowerCase()];
  return translate(t, key || JOURNEY_CATEGORY_I18N_KEYS.automation);
};

export const localizedJourneyActor = (actor, t) => {
  if (actor?.kind === 'system') return translate(t, LABEL_KEYS.systemActor);
  return actor?.name || '';
};

const codeOf = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const firstPresent = (...values) =>
  values.find(value => value != null && value !== '');

const genericTitle = context => translate(context.t, TITLE_KEYS.generic);

const conversationTitle = (context, key) => {
  const displayId = firstPresent(
    context.item.conversationDisplayId,
    context.payload.display_id,
    context.raw.links?.conversation_display_id
  );
  return displayId == null
    ? genericTitle(context)
    : translate(context.t, key, { displayId });
};

const noteTitle = context => {
  const preview = context.payload.content_preview;
  return preview
    ? translate(context.t, TITLE_KEYS.note, { preview })
    : translate(context.t, TITLE_KEYS.noteGeneric);
};

const taskTitle = (context, created = false) => {
  const title =
    firstPresent(context.payload.title, context.payload.task_title) ||
    translate(context.t, LABEL_KEYS.taskUntitled);
  if (created) return translate(context.t, TITLE_KEYS.taskCreated, { title });

  const actionCode = codeOf(
    context.payload.action || context.eventType.slice('task.'.length)
  );
  const actionKey = TASK_ACTION_KEYS[actionCode];
  return actionKey
    ? translate(context.t, TITLE_KEYS.taskActivity, {
        action: translate(context.t, actionKey),
        title,
      })
    : translate(context.t, TITLE_KEYS.taskActivityGeneric, { title });
};

const leadScoreTitle = context => {
  const score = firstPresent(context.payload.score);
  const profileCode =
    LEAD_PROFILE_ALIASES[codeOf(context.payload.lead_profile)];
  const profileKey = LEAD_PROFILE_KEYS[profileCode];
  const profile = profileKey ? translate(context.t, profileKey) : null;
  const variants = {
    'true:true': TITLE_KEYS.leadScoreProfile,
    'true:false': TITLE_KEYS.leadScore,
    'false:true': TITLE_KEYS.leadScorePendingProfile,
    'false:false': TITLE_KEYS.leadScorePending,
  };
  const key = variants[`${score != null}:${profile != null}`];
  return translate(context.t, key, { score, profile });
};

const appointmentTitle = context => {
  const statusCode = codeOf(
    context.payload.status || context.eventType.slice('appointment.'.length)
  );
  const statusKey =
    APPOINTMENT_STATUS_KEYS[statusCode] ||
    'AUTOMATION_EVENTS.EVENT_LABELS.APPOINTMENT_STATUSES.UNKNOWN';
  const statusLabel = translate(context.t, statusKey);
  const date = formatJourneyDateTime(
    context.payload.scheduled_at,
    context.locale
  );
  return date
    ? translate(context.t, TITLE_KEYS.appointmentDate, { statusLabel, date })
    : translate(context.t, TITLE_KEYS.appointment, { statusLabel });
};

const callTitle = context => {
  const directionKey =
    CALL_DIRECTION_KEYS[codeOf(context.payload.direction)] ||
    'AUTOMATION_EVENTS.EVENT_LABELS.CALL_DIRECTIONS.UNKNOWN';
  const statusCode = codeOf(
    context.payload.final_status || context.eventType.slice('call.'.length)
  );
  const statusKey =
    CALL_STATUS_KEYS[statusCode] ||
    'AUTOMATION_EVENTS.EVENT_LABELS.CALL_STATUSES.UNKNOWN';
  const durationNumber = Number(context.payload.duration_seconds);
  const duration =
    Number.isFinite(durationNumber) && durationNumber > 0
      ? durationNumber
      : null;
  const disposition = context.payload.disposition || null;
  const variants = {
    'true:true': TITLE_KEYS.callDurationDisposition,
    'true:false': TITLE_KEYS.callDuration,
    'false:true': TITLE_KEYS.callDisposition,
    'false:false': TITLE_KEYS.call,
  };
  return translate(
    context.t,
    variants[`${duration != null}:${Boolean(disposition)}`],
    {
      direction: translate(context.t, directionKey),
      statusLabel: translate(context.t, statusKey),
      duration,
      disposition,
    }
  );
};

const offerTitle = (context, closing) => {
  const rawTitle = firstPresent(
    context.payload.title,
    context.payload.product_name
  );
  const title =
    !rawTitle || rawTitle === 'Oferta'
      ? translate(context.t, LABEL_KEYS.offerFallback)
      : rawTitle;
  const variants = {
    'false:false': TITLE_KEYS.offer,
    'false:true': TITLE_KEYS.offerAmount,
    'true:false': TITLE_KEYS.offerClosed,
    'true:true': TITLE_KEYS.offerClosedAmount,
  };
  return translate(
    context.t,
    variants[`${closing}:${context.amount != null}`],
    {
      title,
      amount: context.amount,
    }
  );
};

const kanbanCardLabel = context =>
  context.payload.item_title ||
  (context.item.resourceId != null
    ? translate(context.t, LABEL_KEYS.kanbanCard, {
        id: context.item.resourceId,
      })
    : translate(context.t, LABEL_KEYS.kanbanCardUnknown));

const stageTitle = (context, created) => {
  const card = kanbanCardLabel(context);
  const unknownStage = translate(context.t, LABEL_KEYS.kanbanStageUnknown);
  const toLabel = context.payload.new_stage_label || unknownStage;
  return created
    ? translate(context.t, TITLE_KEYS.itemCreated, {
        card,
        stageLabel: toLabel,
      })
    : translate(context.t, TITLE_KEYS.stageChanged, {
        card,
        fromLabel: context.payload.old_stage_label || unknownStage,
        toLabel,
      });
};

const automationTitle = context => {
  if (!context.eventType) return genericTitle(context);
  const product = firstPresent(
    context.payload.product_name,
    context.payload.product_code
  );
  const variant = `${Boolean(product)}:${context.amount != null}`;
  const variants = {
    'true:true': TITLE_KEYS.automationAmount,
    'true:false': TITLE_KEYS.automation,
    'false:true': TITLE_KEYS.automationAmountOnly,
    'false:false': TITLE_KEYS.automationPlain,
  };
  const params = {
    'true:true': {
      eventType: context.eventType,
      product,
      amount: context.amount,
    },
    'true:false': { eventType: context.eventType, product },
    'false:true': { eventType: context.eventType, amount: context.amount },
    'false:false': { eventType: context.eventType },
  };
  return translate(context.t, variants[variant], params[variant]);
};

const EXACT_TITLE_BUILDERS = Object.freeze({
  'conversation.opened': context =>
    conversationTitle(context, TITLE_KEYS.conversationOpened),
  'conversation.first_reply': context =>
    conversationTitle(context, TITLE_KEYS.conversationFirstReply),
  'note.created': noteTitle,
  'task.created': context => taskTitle(context, true),
  'lead_score.scored': leadScoreTitle,
  'kanban.offer.associated': context => offerTitle(context, false),
  'kanban.offer.closed': context => offerTitle(context, true),
  'kanban.stage_changed': context => stageTitle(context, false),
  'kanban.item_created': context => stageTitle(context, true),
});

const PREFIX_TITLE_BUILDERS = Object.freeze([
  ['task.', taskTitle],
  ['appointment.', appointmentTitle],
  ['call.', callTitle],
]);

const RESOURCE_TITLE_BUILDERS = Object.freeze({
  automation_event: automationTitle,
  lead_score: leadScoreTitle,
  lead_appointment: appointmentTitle,
  call_record: callTitle,
});

const buildTitleContext = (item, t, locale) => {
  const raw = item.raw || {};
  const payload = item.payload || raw.payload || {};
  const amountCents =
    item.amountCents != null ? item.amountCents : payload.amount_cents;
  return {
    item,
    raw,
    payload,
    t,
    locale: normalizeJourneyLocale(locale),
    eventType: String(item.eventType || raw.type || ''),
    resourceType: item.resourceType || raw.links?.resource_type || '',
    amount:
      amountCents == null
        ? null
        : formatJourneyMoney(
            amountCents,
            item.currency || payload.currency,
            locale
          ),
  };
};

/**
 * Rebuilds titles from the exact structured fields emitted by the nine Journey
 * adapters. Unknown summaries are reused only in PT-BR; EN/ES always get a safe
 * localized fallback.
 */
export const buildLocalizedTitle = (item, t, locale = 'en') => {
  if (!item) return '';
  if (typeof t !== 'function') return item.title || String(item.id || '');

  const context = buildTitleContext(item, t, locale);
  const resourceBuilder = RESOURCE_TITLE_BUILDERS[context.resourceType];
  const builder =
    context.resourceType === 'automation_event'
      ? resourceBuilder
      : EXACT_TITLE_BUILDERS[context.eventType] ||
        PREFIX_TITLE_BUILDERS.find(([prefix]) =>
          context.eventType.startsWith(prefix)
        )?.[1] ||
        resourceBuilder;

  if (builder) return builder(context);
  if (normalizeJourneyLocale(locale) === 'pt-BR' && item.title)
    return item.title;
  return genericTitle(context);
};
